import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { useWatch } from 'react-hook-form';
import { GovForm } from '@gov-design-system-ce/react-form-core';
import { useAxiosInstance } from '@gov-nx/api/axios';
import { usePoMutation } from '@gov-nx/api/hooks/usePoMutation';
import { newDataBoxesMessageQuery } from '@gov-nx/api/queries/dataBoxQuery';
import { useDataBoxEvents } from '@gov-nx/core/events/DataBox/useDataBoxEvents';
import { useMessageEvents } from '@gov-nx/core/events/Message/useMessageEvents';
import { useProcessControl } from '@gov-nx/core/hooks/Control/useProcessControl';
import type { FileUploadItem } from '@gov-nx/core/hooks/File/fileUpload.types';
import { useFileUpload } from '@gov-nx/core/hooks/File/useFileUpload';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import { compose } from '@gov-nx/core/types/compose';
import type { Nullable } from '@gov-nx/core/types/helpers.types';
import { PageCode } from '@gov-nx/module/page/definitions/codes';
import { useDataBoxStoreLists } from '@gov-nx/store/data-box/store';
import { formatBytes } from '@gov-nx/utils/common/File/file-size';
import { useBoolean } from '@gov-nx/utils/common/Hooks/useBoolean';
import { useDataBoxMatomo } from '../hooks/useDataBoxMatomo';
import { useDocumentsModal } from '../hooks/useDocumentsModal';
import { useLowCreditPrompt } from '../hooks/useLowCreditPrompt';
import { useMessagePrice } from '../hooks/useMessagePrice';
import {
	prepareNewMessageSubmitData,
	useNewMessageForm,
} from '../hooks/useNewMessageForm';
import {
	DataBoxFileUploadParams,
	MAX_ATTACHMENTS,
	MAX_SUM_FILE_SIZE,
} from '../options/options';
import { hasAdditionalDataValues } from '../utils/additionalData';
import type { DataBoxFolderType } from './DataBoxes.types';
import type {
	AdditionalDataInputs,
	DataBoxMessageFormContextType,
	DataboxMessageFormReactionsType,
} from './MessageFormContext.types';
import { useSender } from './useSender';

const DataBoxMessageFormContext =
	createContext<Nullable<DataBoxMessageFormContextType>>(null);

type DataBoxMessageFormProviderProps = {
	dataBoxId?: string;
	folderType?: DataBoxFolderType;
	messageId?: number;
	onMessageSent: () => void;
	onMessageCancelled: () => void;
	children: React.ReactNode;
	reaction?: DataboxMessageFormReactionsType;
	isWeb?: boolean;
};

export function DataBoxMessageFormProvider({
	dataBoxId,
	folderType,
	messageId,
	children,
	onMessageSent,
	onMessageCancelled,
	reaction,
	isWeb = false,
}: DataBoxMessageFormProviderProps) {
	const { trackEvent } = useDataBoxMatomo();
	const { t: tn } = useLocale(PageCode['datove-schranky']);
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);
	const { creditsRefetch } = useDataBoxEvents();
	const { controls, setControls } = useProcessControl();
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { form, formSchema, formDefinition, isFormDataLoading } =
		useNewMessageForm(dataBoxId, folderType, messageId, reaction, isWeb);
	const { documents, showDocumentsModal, setShowDocumentsModal } =
		useDocumentsModal();
	const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
	const { connectedDataBoxes } = useDataBoxStoreLists();
	const dataBoxes = connectedDataBoxes;
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showAdditionalDataModal, setShowAdditionalDataModal] = useState(false);
	const showCancelConfirmModal = useBoolean(false);
	const { axiosInstance } = useAxiosInstance();

	const submitMutation = usePoMutation({
		mutationFn: compose(
			newDataBoxesMessageQuery(axiosInstance),
			prepareNewMessageSubmitData
		),
		onError: (error: Error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async () => {
			setControls({ processLoading: false });
			creditsRefetch();

			toastMessageSuccess('Zpráva odeslána', {
				time: 3000,
			});

			trackEvent({ action: 'submit' });

			if (onMessageSent) {
				onMessageSent();
			}

			form.reset();
		},
	});
	const localData = useWatch({
		control: form.control,
	});

	const canSendForm = Boolean(
		localData.senderId &&
		localData.recipientAutocomplete?.selected &&
		localData.subject &&
		(localData.message ||
			localData.documents?.length ||
			localData.files?.length)
	);
	const formIsTouched = form.formState.isDirty;

	const hasAdditionalInfo = hasAdditionalDataValues(localData);
	const [pager, setPager] = useState({
		page: 1,
		perPage: 10,
	});
	const pageDocuments = documents.slice(
		(pager.page - 1) * pager.perPage,
		pager.page * pager.perPage
	);

	const goToPage = (page: number) => {
		setPager({ ...pager, page });
	};

	const resetDocuments = useCallback(() => {
		selectedDocuments.forEach((documentId) => {
			form.setValue(`document${documentId}`, false);
		});
		localData.documents?.forEach((documentId) => {
			form.setValue(`document${documentId}`, true);
		});
		setSelectedDocuments([...(localData.documents || [])]);
	}, [localData.documents, selectedDocuments, form]);

	const onDocumentsModalClose = useCallback(() => {
		resetDocuments();
		setShowDocumentsModal(false);
		setPager({
			...pager,
			page: 1,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [
		setShowDocumentsModal,
		localData.documents,
		selectedDocuments,
		form,
		pager,
	]);

	const onUploadModalClose = useCallback(() => {
		setShowUploadModal(false);
	}, [setShowUploadModal]);

	const resetAdditionalData = useCallback(() => {
		form.setValue(
			'temporary.additionalData.toTheHandsOf',
			localData.toTheHandsOf
		);
		form.setValue(
			'temporary.additionalData.intoTheirOwnHands',
			localData.intoTheirOwnHands
		);
		form.setValue(
			'temporary.additionalData.senderIdentification',
			localData.senderIdentification
		);
		form.setValue(
			'temporary.additionalData.naseCisloJednaci',
			localData.naseCisloJednaci
		);
		form.setValue(
			'temporary.additionalData.naseSpisovaZnacka',
			localData.naseSpisovaZnacka
		);
		form.setValue(
			'temporary.additionalData.vaseCisloJednaci',
			localData.vaseCisloJednaci
		);
		form.setValue(
			'temporary.additionalData.vaseSpisovaZnacka',
			localData.vaseSpisovaZnacka
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [localData]);

	const onAdditionalDataModalConfirm = useCallback(() => {
		setShowAdditionalDataModal(false);
		form.setValue(
			'toTheHandsOf',
			form.getValues('temporary.additionalData').toTheHandsOf
		);
		form.setValue(
			'intoTheirOwnHands',
			form.getValues('temporary.additionalData').intoTheirOwnHands
		);
		form.setValue(
			'senderIdentification',
			form.getValues('temporary.additionalData').senderIdentification
		);
		form.setValue(
			'naseCisloJednaci',
			form.getValues('temporary.additionalData').naseCisloJednaci
		);
		form.setValue(
			'naseSpisovaZnacka',
			form.getValues('temporary.additionalData').naseSpisovaZnacka
		);
		form.setValue(
			'vaseCisloJednaci',
			form.getValues('temporary.additionalData').vaseCisloJednaci
		);
		form.setValue(
			'vaseSpisovaZnacka',
			form.getValues('temporary.additionalData').vaseSpisovaZnacka
		);

		toastMessageSuccess(
			tn('nova-zprava.doplnkove-udaje.pridany-doplnkove-udaje')
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [setShowAdditionalDataModal]);

	const onAdditionalDataModalClose = useCallback(() => {
		resetAdditionalData();
		setShowAdditionalDataModal(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [setShowAdditionalDataModal, localData]);

	const removeAdditionalData = useCallback(
		(key: keyof AdditionalDataInputs) => {
			if (key === 'intoTheirOwnHands' || key === 'senderIdentification') {
				form.setValue(key, false);
				form.setValue(`temporary.additionalData.${key}`, false);
			} else {
				form.setValue(key, '');
				form.setValue(`temporary.additionalData.${key}`, '');
			}
		},
		[form]
	);

	const openDocumentsModal = useCallback(() => {
		setShowDocumentsModal(true);
	}, [setShowDocumentsModal]);

	const removeFile = useCallback(
		(fileId: string) => {
			form.setValue('files', [
				...((localData.files?.filter((file) => file.fileId !== fileId) ||
					[]) as FileUploadItem[]),
			]);
		},
		[form, localData.files]
	);

	const openUploadModal = useCallback(() => {
		setShowUploadModal(true);
	}, [setShowUploadModal]);

	const openAdditionalDataModal = useCallback(() => {
		resetAdditionalData();
		setShowAdditionalDataModal(true);
	}, [resetAdditionalData]);

	const onRecipientRemove = useCallback(() => {
		form.setValue('recipientAutocomplete', undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [form]);

	const addDocument = useCallback(
		(documentId: number) => {
			setSelectedDocuments([...selectedDocuments, documentId]);
		},
		[selectedDocuments]
	);

	const removeDocument = useCallback(
		(documentId: number, isTemporary = false) => {
			const newValue = [
				...selectedDocuments.filter((item) => item !== documentId),
			];

			setSelectedDocuments([...newValue]);

			if (!isTemporary) {
				form.setValue(`document${documentId}`, false);
				form.setValue('documents', [...newValue]);
			}
		},
		[form, selectedDocuments]
	);

	const onFilesConfirm = useCallback(() => {
		const currentFiles = form.getValues('files');
		let filesCount = currentFiles?.length ?? 0;
		let totalFilesSize =
			currentFiles?.reduce((acc, file) => acc + (file.size ?? 0), 0) ?? 0;

		const tempFiles = localData.temporary?.files;
		const addedFiles = [];
		let filesOverflow = false;

		if (tempFiles && tempFiles.length > 0) {
			for (const file of tempFiles) {
				totalFilesSize += file.size ?? 0;
				filesCount++;
				if (
					filesCount > MAX_ATTACHMENTS ||
					totalFilesSize > MAX_SUM_FILE_SIZE
				) {
					filesOverflow = true;
					break;
				} else {
					addedFiles.push(file);
				}
			}

			form.setValue('files', [
				...((currentFiles || []) as FileUploadItem[]),
				...(addedFiles as FileUploadItem[]),
			]);
			form.setValue('temporary.files', []);

			if (filesOverflow) {
				toastMessageError(
					t('nahrani-souboru.pocet-velikost-souboru', {
						count: MAX_ATTACHMENTS,
						sumSize: formatBytes(MAX_SUM_FILE_SIZE),
					})
				);
			} else {
				toastMessageSuccess(
					tn('nova-zprava.prilohy.pridany-prilohy', {
						count: localData.temporary?.files?.length ?? 0,
					}),
					{
						time: 3000,
					}
				);
			}
		}

		onUploadModalClose();
	}, [
		form,
		localData.temporary?.files,
		onUploadModalClose,
		toastMessageError,
		t,
		toastMessageSuccess,
		tn,
	]);
	const onDocumentsConfirm = useCallback(() => {
		form.setValue('documents', selectedDocuments.filter(Boolean));
		toastMessageSuccess(
			tn('nova-zprava.prilohy.pridany-prilohy', {
				count: selectedDocuments.length,
			}),
			{
				time: 3000,
			}
		);
		setShowDocumentsModal(false);
		setPager({
			...pager,
			page: 1,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [toastMessageSuccess, tn, selectedDocuments.length, pager]);

	const { hideMessageList, resetActionInfo } = useDataBoxEvents();

	useEffect(() => {
		setTimeout(() => {
			hideMessageList({ hidden: true });
		});

		return () => {
			setTimeout(() => {
				hideMessageList({ hidden: false });
				resetActionInfo();
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	useEffect(() => {
		if (Object.keys(form.formState.touchedFields).length > 0) {
			form.trigger('message');
		}
	}, [localData.documents, localData.files, form]);

	const recipient = form.watch('recipientAutocomplete')?.selected;

	const sender = useSender(dataBoxes, form);

	const messagePrice = useMessagePrice(form, localData.senderId);

	const lowCreditPrompt = useLowCreditPrompt({
		senderId: localData.senderId,
		recipient: recipient,
		messagePrice: messagePrice.price,
		onPromptClose: onRecipientRemove,
	});

	const handleSubmit = useCallback(async () => {
		if (messagePrice.isLowCredit) {
			lowCreditPrompt.handleOpen();
			return;
		}
		setControls({ processError: null, processLoading: true });
		submitMutation.mutate(form.getValues());
	}, [
		form,
		setControls,
		submitMutation,
		lowCreditPrompt,
		messagePrice.isLowCredit,
	]);

	const onSubmit = form.handleSubmit(handleSubmit);

	const fileUploadHook = useFileUpload(
		form,
		'temporary.files',
		DataBoxFileUploadParams
	);

	return (
		<DataBoxMessageFormContext.Provider
			value={{
				dataBoxId,
				folderType,
				messageId,
				formSchema,
				controls,
				submitMutation,
				dataBoxes,
				recipient,
				sender,
				showDocumentsModal,
				showUploadModal,
				showAdditionalDataModal,
				documents,
				selectedDocuments,
				blockUploadModalClose:
					fileUploadHook.filesUploading || fileUploadHook.filesValidating,
				hasAdditionalInfo,
				isLoading: !dataBoxes || dataBoxes.length === 0 || isFormDataLoading,
				pageDocuments,
				pager,
				showCancelConfirmModal,
				canSendForm,
				localData,
				messagePrice,
				lowCreditPrompt,
				formIsTouched,
				goToPage,
				onDocumentsModalClose,
				onSubmit,
				setControls,
				onUploadModalClose,
				onAdditionalDataModalClose,
				onAdditionalDataModalConfirm,
				removeAdditionalData,
				openDocumentsModal,
				openUploadModal,
				openAdditionalDataModal,
				onRecipientRemove,
				addDocument,
				removeDocument,
				onFilesConfirm,
				onDocumentsConfirm,
				onMessageCancelled,
				removeFile,
				fileUploadHook,
			}}>
			<GovForm formDefinition={formDefinition}>{children}</GovForm>
		</DataBoxMessageFormContext.Provider>
	);
}

export const useDataBoxMessageFormContext = (): DataBoxMessageFormContextType =>
	useContext(DataBoxMessageFormContext) as DataBoxMessageFormContextType;
